/* eslint-disable ember/no-computed-properties-in-native-classes */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { get, set, action, computed } from '@ember/object';
import { sum, mapBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class LineItemDetailController extends Controller {
  @action
  set_reportTitle(value) {
    this._reportTitle = value;
  }

  @service
  strings;

  @tracked from = '';
  @tracked to = '';
  @tracked
  filters = [];

  @mapBy('filteredData', 'displayAmount')
  debitAmounts;

  @sum('debitAmounts')
  debitTotal;

  @mapBy('filteredData', 'receipts')
  receiptsAmounts;

  @sum('receiptsAmounts')
  receiptsTotal;

  @mapBy('filteredData', 'otherCredits')
  otherCreditsAmounts;

  @sum('otherCreditsAmounts')
  otherCreditsTotal;

  @mapBy('filteredData', 'balance')
  balanceAmounts;

  @sum('balanceAmounts')
  balanceTotal;

  @tracked _reportTitle = null;

  get reportTitle() {
    if (this._reportTitle !== null) {
      return this._reportTitle;
    }
    return `Line Item Detail: ${this.from} - ${this.to}`;
  }

  // filteredData applies the user's filters to the data array computed above.
  @computed('model.[]', 'filters.@each.{field,value}')
  get filteredData() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.model;
    }

    // Map raw filters into what we need to actually perform the filtering.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;
      switch (filter.field) {
        case 'jurisdictionalId':
        case 'firstName':
        case 'lastName':
        case 'homeroom.name':
          key = `account.person.${filter.field}`;
          break;
        case 'currentYearLevel':
          key = `account.person.${filter.field}`;
          dataType = DataType.INTEGER;
          break;
        case 'displayAmount':
        case 'receipts':
        case 'otherCredits':
        case 'balance':
          dataType = DataType.FLOAT;
          break;
      }

      return {
        key,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return this.model.filter((row) => {
      return filters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  reset() {
    this.filters = [];
  }
}
